import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { from, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

/**
 * Forces every authenticated user past the legal-acceptance screen before
 * they reach a protected route. Covers the edge case where a brand-new
 * Google user signs in via login mode (no checkbox shown there) — see
 * .doc/pending-booking-flow.md #10d.
 *
 * Runs after authGuard, which guarantees `firebaseUser` is non-null here.
 * Apply only to routes that should NOT be reachable without acceptance —
 * critically, do NOT chain this on `/aceptar-terminos` itself or you'll
 * create an infinite redirect.
 */
export const legalGuard: CanActivateFn = (_route, state) => {
  const auth      = inject(Auth);
  const firestore = inject(Firestore);
  const router    = inject(Router);
  // Capture the injector once while we're still in the guard's DI scope.
  // Firebase API calls made later inside RxJS callbacks (switchMap) run
  // outside that scope, so we re-enter it via runInInjectionContext.
  const injector  = inject(EnvironmentInjector);

  return authState(auth).pipe(
    take(1),
    switchMap(firebaseUser => {
      // No user → let authGuard handle the redirect to /login.
      if (!firebaseUser) return of(true as const);
      const fetch = runInInjectionContext(injector, () =>
        getDoc(doc(firestore, 'users', firebaseUser.uid)),
      );
      return from(fetch).pipe(
        map(snap => {
          const data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
          if (data?.['legalAccepted'] === true) return true;
          return router.createUrlTree(['/aceptar-terminos'], {
            queryParams: { returnUrl: state.url },
          });
        }),
      );
    }),
  );
};
